import { Modal, Row, Col, Typography, Button, Steps, Image, Table } from 'antd';
import styles from '../Style/DownloadApi.module.scss';
import React, { useState } from 'react';
import { Prism, SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  checkRequestStatusCode,
  downloadDataCode,
  getAllDatasetCode,
  links,
  stepsData,
  submitExtractionCode,
  datasetTable,
  extractionTable,
  statusTable,
  extractionV2,
  titles,
  getAllDatasetTitles,
  authTitles,
  authCode,
} from './const';
import CodeHighlighter from './CodeHighlighter';
import { useNavigate } from 'react-router-dom';
import { useSectionScrollSync } from '../hooks/useSectionScrollSync';

const { Title, Paragraph, Text } = Typography;

type PrivateProps = {
  visible: boolean;
  onHandleDownloadApiModalCancel: () => void;
};

const { Step } = Steps;

const sectionSlugs = links.map((link) => link.id);

const SyntaxHighlighter = Prism as any as React.FC<SyntaxHighlighterProps>;

const CustomIcon: React.FC<{ index: number }> = ({ index }) => (
  <div
    style={{
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: 'white',
      border: 'solid 1px #1890ff',
      color: '#1890ff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '16px',
      fontWeight: 'bold',
    }}
  >
    {index + 1}
  </div>
);

const StepContent: React.FC<{
  title: string;
  description: string;
  image?: string;
}> = ({ description, image }) => (
  <div>
    <p>{description}</p>
    {image && (
      <Image src={image} alt="Valid api key" style={{ maxWidth: '100%', marginTop: '10px' }} />
    )}
  </div>
);

export default function DownloadApiModal({
  visible,
  onHandleDownloadApiModalCancel,
}: PrivateProps) {
  const [activeLang, setActiveLang] = useState<'python' | 'matlab'>('python');
  const navigate = useNavigate();
  const { activeLink, contentRef, registerSection, goToSection } = useSectionScrollSync(
    visible,
    sectionSlugs
  );

  return (
    <Modal
      className={styles.downloadModal}
      open={visible}
      title={[
        <div className={styles.titleContainer} key={'download-modal-title-container'}>
          <h1>MOOD API Documentation</h1>
        </div>,
      ]}
      footer={null}
      onCancel={onHandleDownloadApiModalCancel}
      bodyStyle={{ overflowY: 'scroll' }}
      width={1200}
      zIndex={1000}
    >
      <div className={styles.downloadModalContent}>
        <div className={styles.listNavigation}>
          {links.map((link) => (
            <a
              key={link.id}
              className={`${activeLink === link.id ? styles.active : ''}`}
              href={`#${link.id}`}
              onClick={(e) => {
                e.preventDefault();
                goToSection(link.id);
              }}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className={styles.content} ref={contentRef}>
          <Row className={styles.stickyNav} align="middle" justify="end">
            <Col>
              <Text strong style={{ marginRight: '12px' }}>
                Show examples in:
              </Text>
              <Button
                type={activeLang === 'python' ? 'primary' : 'default'}
                onClick={() => setActiveLang('python')}
                style={{ marginRight: '8px', borderRadius: '25px' }}
              >
                Python
              </Button>
              <Button
                type={activeLang === 'matlab' ? 'primary' : 'default'}
                style={{ borderRadius: '25px' }}
                onClick={() => setActiveLang('matlab')}
              >
                Matlab
              </Button>
            </Col>
          </Row>
          <section id="intro" ref={registerSection('intro')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>About Mood API</Title>
                <Paragraph>
                  Welcome to Metocean Data Portal API. This API helps the user to download the
                  selected datasets in
                  <span
                    className={styles.spanLink}
                    onClick={() => {
                      navigate('/');
                    }}
                  >
                    https://www.metocean-on-demand.com
                  </span>
                  (MOOD) without going through the website.
                </Paragraph>
                <Paragraph>
                  To ensure the system stability, we have limited the max number of requests made by
                  each client within 1 minute. Please put some time interval between each request or
                  API call to prevent your requests being blocked. The system is not meant to
                  request large number of datasets as this will block the system. If you are
                  interested in multiple (hundreds) of datasets, it is possible to order an HDD
                  containing all the requested data.
                </Paragraph>
                <Paragraph>
                  A unique API key is required for each data request. All API calls in our system
                  require this key be passed as a query parameter to authenticate the request. To
                  start the data download, kindly get a API key through this website (as step 1).
                  Then, use the API key in the following steps for the data request.
                </Paragraph>
                <Paragraph>
                  In this help page, we have provided examples of API calling in following
                  languages: Python3 and Matlab (version 2016a or later). The examples in different
                  languages can be seen by selecting the language from the top right side of the
                  screen.
                </Paragraph>
                <Paragraph>
                  During extraction, the API will first check if the selected location is inside the
                  range of the selected dataset. If the location is inside the boundary of the
                  dataset, the API will then select a nearest grid point in our data storage to
                  process the result files. Otherwise, an error message will be shown.
                </Paragraph>
              </Col>
              <Col></Col>
            </Row>
          </section>
          <section id="auth" ref={registerSection('auth')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>Authentication</Title>
                <Paragraph>All request must include the following headers:</Paragraph>
                <Table
                  title={() => <strong>Request Headers</strong>}
                  columns={datasetTable.columns}
                  dataSource={datasetTable.dataSource}
                  pagination={false}
                  bordered
                  size="small"
                />
              </Col>
              <Col span={12}>
                <CodeHighlighter
                  activeLang={activeLang}
                  codeData={authCode}
                  titles={authTitles}
                  key="extraction"
                />
              </Col>
            </Row>
          </section>
          <section id="api-key" ref={registerSection('api-key')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>1. API key request</Title>
                <Paragraph>Follow these steps to obtain an API key:</Paragraph>
                <Steps direction="vertical" current={stepsData.length - 1}>
                  {stepsData.map((step, index) => (
                    <Step
                      key={index}
                      title={step.title}
                      description={
                        <StepContent
                          description={step.description}
                          image={step.image}
                          title={step.title}
                        />
                      }
                      icon={<CustomIcon index={index} />}
                    />
                  ))}
                </Steps>
                <Paragraph>
                  The API key has a fixed lifetime and periodic updates will be required. An API key
                  is assigned to a single user after the user accepts the terms of using the API
                  key. The API key may not be shared with other users .
                </Paragraph>
              </Col>
            </Row>
          </section>
          <section id="dataset" ref={registerSection('dataset')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>2. Get all datasets</Title>
                <Paragraph>
                  Returns a list of all available datasets for download, including dataset name,
                  description, start time, end time, cost, supported formats and download file
                  format which the download link will point to.
                </Paragraph>
                <Paragraph>
                  The following table shows the details of the parameters in the request headers. It
                  will return all the free datasets available in our system. For subscribed users,
                  it will return all the subscribed datasets.
                </Paragraph>
              </Col>
              <Col span={12}>
                <CodeHighlighter
                  activeLang={activeLang}
                  codeData={getAllDatasetCode}
                  titles={getAllDatasetTitles}
                  key={'datasets'}
                />
              </Col>
            </Row>
          </section>
          <section id="extraction" ref={registerSection('extraction')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>3. Submit extraction request (LEGACY)</Title>
                <Paragraph>
                  NOTICE: This method is no longer recommended. Please use &quot;Submit extraction
                  and check status (NEW)&quot; instead.
                </Paragraph>
                <Paragraph>
                  Submit a new extraction request. It will return an order ID for user to check the
                  status in &quot;Check request status&quot;.
                </Paragraph>
                <Paragraph>
                  The extraction limit is 15 extractions/day. This is a POST request and a JSON body
                  is required. The following table shows the details of the parameters in the body.
                </Paragraph>
                <br />
                <Paragraph>
                  <Table
                    title={() => <strong>Request Body</strong>}
                    columns={extractionTable.columns}
                    dataSource={extractionTable.dataSource}
                    pagination={false}
                    bordered
                    size="small"
                  />
                </Paragraph>
              </Col>
              <Col span={12}>
                <CodeHighlighter
                  activeLang={activeLang}
                  codeData={submitExtractionCode}
                  titles={titles}
                  key="extraction"
                />
              </Col>
            </Row>
          </section>
          <section id="request" ref={registerSection('request')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>4. Check request status (LEGACY)</Title>
                <Paragraph>
                  Check the request status. It will return &quot;Pending&quot; if the request is
                  still under processing, or return a download URL if the extraction is completed.
                </Paragraph>
                <Paragraph>
                  This is a POST request and a JSON body is required. The following table shows the
                  details of the parameters in the body.
                </Paragraph>
                <br />
                <Paragraph>
                  <Table
                    title={() => <strong>Request Body</strong>}
                    columns={statusTable.columns}
                    dataSource={statusTable.dataSource}
                    pagination={false}
                    bordered
                    size="small"
                  />
                </Paragraph>
              </Col>
              <Col span={12}>
                <CodeHighlighter
                  activeLang={activeLang}
                  codeData={checkRequestStatusCode}
                  titles={titles}
                  key="extraction"
                />
              </Col>
            </Row>
          </section>
          <section id="extraction-v2" ref={registerSection('extraction-v2')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>5. Submit extraction and check status (NEW)</Title>
                <Paragraph>
                  Submit a new extraction request. It will return an order ID for user to check the
                  status in &quot;Check request status&quot;. Compared to the &quot;Submit
                  extraction request (LEGACY)&quot;, this method allows user to retrieve the order
                  identification immediately after submission without waiting for the response and
                  use it in &quot;Check request status&quot; to check the extraction status. Code
                  snippets for both submission and status checking are provided in this section.
                </Paragraph>
                <Paragraph>
                  The extraction limit is 15 extractions/day. This is a POST request and a JSON body
                  is required. The following table shows the details of the parameters in the body.
                </Paragraph>
                <Paragraph>
                  <Table
                    title={() => <strong>Request Body</strong>}
                    columns={extractionTable.columns}
                    dataSource={extractionTable.dataSource}
                    pagination={false}
                    bordered
                    size="small"
                  />
                </Paragraph>
              </Col>
              <Col span={12}>
                <CodeHighlighter
                  activeLang={activeLang}
                  codeData={extractionV2}
                  titles={titles}
                  key="extraction-v2"
                />
              </Col>
            </Row>
          </section>
          <section id="download" ref={registerSection('download')}>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>6. Download data</Title>
                <Paragraph>
                  Download the data based on the download link returned from &quot;Check request
                  status&quot;. The download link will be available for 1 day from the date of
                  request.
                </Paragraph>
                <Paragraph>
                  In the example scripts provided, the saving file name is hardcoded such that the
                  file name will not be duplicated for every Order ID. You may also set your own
                  file name, but please pay attention to avoid file name duplication as it is not
                  handled in the script
                </Paragraph>
                <Paragraph>
                  Please pay attention to the &quot;Download Format&quot; for different datasets,
                  referring to step 2, and set the saving format accordingly in the script. Data
                  will become available in zip format.
                </Paragraph>
              </Col>
              <Col span={12}>
                <SyntaxHighlighter
                  language={activeLang}
                  style={vscDarkPlus}
                  customStyle={{
                    borderRadius: '0.375rem',
                  }}
                >
                  {downloadDataCode[activeLang]}
                </SyntaxHighlighter>
                <br />
              </Col>
            </Row>
          </section>
        </div>
      </div>
    </Modal>
  );
}
